import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DonutChart } from "@fluentui/react-charts";
import "./CSS/AgentStatusWidget.css";

// Ne prend PAS `reasonCodes` : les activités configurées sur la file ne sont pas des
// agents. Les compter créait un agent fantôme par activité (voir calculateDynamicStats).
const AgentStatusWidget = ({ agentStats, agentDetails }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const containerRef = useRef(null);

  // Calculer dynamiquement le nombre d'items affichables selon la hauteur
  useEffect(() => {
    const calculateItemsPerPage = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        // Hauteur approximative : header (30px) + donut (160px) + marges (10px) = 200px
        // Chaque ligne de légende fait environ 20px
        const availableHeight = containerHeight - 200;
        const itemsCanFit = Math.floor(availableHeight / 20);
        const newItemsPerPage = Math.max(6, Math.min(itemsCanFit, 50)); // Min 6, Max 50
        
        if (newItemsPerPage !== itemsPerPage) {
          setItemsPerPage(newItemsPerPage);
          setCurrentPage(0); // Réinitialiser à la première page
        }
      }
    };

    // Calculer immédiatement
    const timer = setTimeout(calculateItemsPerPage, 100);

    // Observer pour détecter les changements de taille
    const resizeObserver = new ResizeObserver(calculateItemsPerPage);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [itemsPerPage]);

  // Heedify brand palette — aligned with HeedifyTheme.css chart colors
  const colorPalette = [
    '#00A4A6', // Teal — primary accent
    '#1B3A6B', // Navy — primary
    '#8B5CF6', // Violet
    '#F97316', // Orange
    '#10b981', // Green
    '#FFC700', // Amber
    '#06B6D4', // Cyan
    '#EF4444', // Red
    '#D946EF', // Fuchsia
    '#22497F'  // Primary hover (navy variant)
  ];

  // Semantic mapping for known agent states
  const getColorForState = (state, index) => {
    const normalizedState = state.toLowerCase().trim();
console.log(`Mapping state "${state}" (normalized: "${normalizedState}") to color...`);
    const stateColorMap = {
      'ready':       '#107C10', // success
      'available':   '#107C10',
      'oncall':      '#00A4A6', // accent teal
      'busy':        '#d13438', // danger
      'admin':       '#1B3A6B', // primary navy
      'coffeebreak': '#FFC700', // warning amber
      'outofshift':  '#A0A3AB', // disabled gray
      'offline':     '#605E5C', // text secondary
      'wrapup':      '#8B5CF6', // violet
      'training':    '#06B6D4'  // cyan
    };

    return stateColorMap[normalizedState] || colorPalette[index % colorPalette.length];
  };

  // Fonction pour formater le label de l'état
  const formatStateLabel = (state) => {
    // Tentative de traduction si disponible
    const translations = {
      'ready': t("REALTIME.AVAILABLE"),
      'available': t("REALTIME.AVAILABLE"),
      'busy': t("REALTIME.UNAVAILABLE"),
      'oncall': t("REALTIME.ON_CALL"),
      'admin': t("REALTIME.ADMIN_WORK"),
      'coffeebreak': t("REALTIME.BREAK"),
      'outofshift': t("REALTIME.OUT_OF_SHIFT"),
      'offline': t("REALTIME.OFFLINE")
    };
    
    const normalized = state.toLowerCase().trim();
    return translations[normalized] || state;
  };

  // Répartition des agents par statut — déduite UNIQUEMENT des agents réels.
  //
  // Ce donut comptait aussi `reasonCodes`, qui est le CATALOGUE des activités configurées
  // sur la file (COFFEE_BREAK, ADMIN WORK, LUNCH, INTERVENTION…) et non l'état d'un agent.
  // Chaque activité configurée était comptée comme UN AGENT dans cet état : elle créait un
  // agent FANTÔME. D'où des statuts affichés à 1 sans qu'aucun agent s'y trouve, et un
  // total qui additionnait de la configuration à des agents réels — une file avec 0 agent
  // connecté pouvait afficher 9 dans le donut.
  //
  // Une activité que personne n'utilise ne doit pas apparaître : le donut montre QUI est
  // dans QUEL statut, pas quels statuts existent. (La liste exhaustive des activités, elle,
  // reste affichée sur la carte de file — c'est son rôle, pas celui du donut.)
  const calculateDynamicStats = () => {
    const stateCounts = {};
    let total = 0;

    // `liveState` = statut EFFECTIF (ce que l'agent peut réellement faire : un agent qui se
    // déclare READY alors que Teams le voit Busy ne reçoit aucun appel). Repli sur `state`
    // (déclaré) tant que fix/simultaneousCalls n'est pas déployée partout.
    if (agentDetails && typeof agentDetails === 'object') {
      Object.values(agentDetails).forEach(agent => {
        const state = agent?.liveState ?? agent?.state;
        if (state) {
          stateCounts[state] = (stateCounts[state] || 0) + 1;
          total++;
        }
      });
    }

    return { stateCounts, total };
  };

  const { stateCounts, total: calculatedTotal } = calculateDynamicStats();

  // On ne bascule sur les stats dynamiques que si au moins un AGENT RÉEL a été compté.
  // Tester `Object.keys(stateCounts).length` ne suffit pas : les activités configurées
  // pré-déclarent désormais des catégories à zéro, donc l'objet n'est jamais vide et le
  // repli sur `agentStats` ne se déclencherait plus jamais.
  const statsToUse = calculatedTotal > 0 ? stateCounts : agentStats;

  // Normaliser et regrouper les états pour éviter les doublons (Available/available/AVAILABLE)
  const normalizedStats = {};
  Object.entries(statsToUse || {}).forEach(([key, count]) => {
    if (key !== 'total' && count > 0) {
      const normalizedKey = key.toLowerCase().trim();
      
      // Si la clé normalisée existe déjà, on additionne
      if (normalizedStats[normalizedKey]) {
        normalizedStats[normalizedKey].count += count;
      } else {
        // Sinon on crée l'entrée avec le label original
        normalizedStats[normalizedKey] = {
          originalKey: key,
          count: count
        };
      }
    }
  });

  // Créer une liste dynamique des états présents
  const statusConfig = Object.entries(normalizedStats)
    .map(([normalizedKey, data], index) => ({
      key: normalizedKey,
      label: formatStateLabel(data.originalKey),
      color: getColorForState(normalizedKey, index),
      count: data.count
    }));

  // Calculer le total réel à partir des données affichées
  const total = statusConfig.reduce((sum, item) => sum + item.count, 0);

  // Pagination
  const totalPages = Math.ceil(statusConfig.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = statusConfig.slice(startIndex, endIndex);

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Préparer les données pour le DonutChart de Fluent UI (toutes les données)
  const chartPoints = statusConfig.map(status => ({
    legend: status.label,
    data: status.count,
    color: status.color,
  }));

  const chartData = {
    chartTitle: t("REALTIME.AGENT_AVAILABILITY"),
    chartData: chartPoints,
  };

  return (
    <div className="agent-status-widget" ref={containerRef}>
      <div className="agent-status-header kpi-card-header">
        <span className="drag-handle">⋮⋮</span>
        <h3>{t("REALTIME.AVAILABILITY")}</h3>
        <div className="agent-status-nav">
          {totalPages > 1 && (
            <>
              <button 
                className="agent-nav-btn" 
                onClick={handlePrevious} 
                disabled={currentPage === 0}
              >
                ←
              </button>
              <span className="agent-page-indicator">
                {currentPage + 1} / {totalPages}
              </span>
              <button 
                className="agent-nav-btn" 
                onClick={handleNext} 
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </>
          )}
        </div>
      </div>
      
      <div className="realtime-donut-chart-container">
        {/* Donut Chart from Fluent UI */}
        <div className="donut-chart-wrapper">
          {chartPoints.length > 0 ? (
            <DonutChart
              culture={typeof window !== "undefined" ? window.navigator.language : "en-us"}
              data={chartData}
              innerRadius={45}
              hideLegend={true}
              height={160}
              width={160}
              valueInsideDonut={total}
              hideLabels={true}
              calloutProps={{
                styles: {
                  root: {
                    maxWidth: '100px',
                    minWidth: '80px',
                  },
                  calloutMain: {
                    padding: '4px 6px',
                    fontSize: '9px',
                  },
                },
              }}
            />
          ) : (
            <div className="no-data">{t("REALTIME.NO_AGENT_DATA")}</div>
          )}
        </div>

        {/* Legend on the right */}
        <div className="status-values-container">
          <div className="status-values-grid">
            {currentPageData.map((item, index) => {
              return (
                <div key={index} className="status-value-item">
                  <div className="status-value-header">
                    <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                    <span className="status-value-label">{item.label}</span>
                  </div>
                  <div className="status-value-percentage">{item.count}</div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination indicator */}
          {totalPages > 1 && (
            <div className="status-pagination-indicator">
              <div className="pagination-dots">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <span
                    key={index}
                    className={`pagination-dot ${index === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(index)}
                  />
                ))}
              </div>
              {currentPage < totalPages - 1 && (
                <div className="more-items-hint">
                  +{statusConfig.length - endIndex} {t("REALTIME.MORE_STATUSES") || "autres statuts"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentStatusWidget;
